<template>
  <div class="container p-4 mapa-cap">
    <div class="flex flex-wrap align-items-center gap-2 mb-3">
      <ButtonPrime icon="pi pi-arrow-left" class="p-button-text" @click="router.push({ name: 'estudyoo-hub' })" />
      <h3 class="text-xl font-bold m-0">Mapa de capítulos y actividades</h3>
    </div>

    <DjangoAuthGate @authenticated="onDjangoAuth" />

    <template v-if="djangoOk">
      <p class="text-sm text-600 mb-3">
        Elige colegio y materia; luego pulsa <strong>Traer todos</strong> para ver todos los capítulos de la asignatura,
        o acota por <strong>nivel</strong> / <strong>curso</strong> y usa <strong>Cargar mapa</strong>.
        Nada se carga hasta que presiones uno de esos botones.
      </p>

      <div class="grid grid-cols-1 md:grid-cols-6 gap-3 mb-3 p-3 border-round surface-border border-1">
        <div>
          <label class="block text-sm mb-1">Colegio</label>
          <Dropdown
            v-model="selColegioId"
            :options="colegioOpts"
            option-label="label"
            option-value="id"
            placeholder="Todos"
            class="w-full"
            filter
            show-clear
            :loading="loadingColegios"
            @change="onColegioChange"
          />
        </div>
        <div>
          <label class="block text-sm mb-1">Materia <span class="text-red-500">*</span></label>
          <Dropdown
            v-model="selMateria"
            :options="materiasFiltradas"
            option-label="nombre"
            placeholder="Elegir materia"
            class="w-full"
            filter
            show-clear
            :loading="loadingCatalogos"
            :disabled="loadingCatalogos"
            @show="cargarCatalogosSiFalta"
            @change="onMateriaChange"
          />
        </div>
        <div>
          <label class="block text-sm mb-1">Nivel</label>
          <Dropdown
            v-model="selNivel"
            :options="nivelOpts"
            option-label="label"
            option-value="value"
            placeholder="Elegir nivel"
            class="w-full"
            filter
            show-clear
            :loading="loadingCatalogos"
            :disabled="!selMateria || loadingCatalogos"
            @show="cargarCatalogosSiFalta"
            @change="onNivelChange"
          />
        </div>
        <div>
          <label class="block text-sm mb-1">Curso</label>
          <Dropdown
            v-model="selCurso"
            :options="cursoOpts"
            option-label="label"
            :placeholder="selNivel ? 'Sección A, B…' : 'Opcional — elige nivel'"
            class="w-full"
            filter
            show-clear
            :disabled="!selMateria"
            @show="cargarCatalogosSiFalta"
          />
        </div>
        <div class="flex flex-wrap align-items-end gap-2 md:col-span-2">
          <ButtonPrime
            label="Traer todos"
            icon="pi pi-list"
            class="p-button-outlined"
            :loading="loading && modoCarga === 'todos'"
            :disabled="loading"
            @click="traerTodos"
          />
          <ButtonPrime
            label="Cargar mapa"
            icon="pi pi-sitemap"
            :loading="loading && modoCarga === 'filtrado'"
            :disabled="loading"
            @click="cargarMapa"
          />
          <ButtonPrime label="Limpiar" icon="pi pi-filter-slash" class="p-button-outlined" @click="limpiar" />
        </div>
      </div>

      <p v-if="!catalogListo && !loadingCatalogos" class="text-xs text-500 mb-3">
        Abre el combo de materia o pulsa «Traer todos» / «Cargar mapa» para cargar el catálogo de filtros.
      </p>

      <div
        v-if="loadingCatalogos"
        class="mb-3 p-3 border-round border-1 surface-border flex align-items-center gap-2 text-sm text-600"
      >
        <i class="pi pi-spin pi-spinner" />
        Cargando materias y cursos…
      </div>

      <div v-if="mapa?.aviso" class="mb-3 p-3 border-round bg-orange-50 text-sm text-orange-900 border-1 border-orange-200">
        {{ mapa.aviso }}
      </div>

      <div v-if="filtrosActivos" class="flex flex-wrap gap-2 mb-3">
        <div class="flex align-items-center gap-2">
          <Checkbox v-model="soloSinActividades" input-id="filt-sin-act" :binary="true" />
          <label for="filt-sin-act" class="text-sm cursor-pointer">Solo capítulos sin actividades</label>
        </div>
        <div v-if="selCurso" class="flex align-items-center gap-2">
          <Checkbox v-model="soloSinAsignacion" input-id="filt-sin-asig" :binary="true" />
          <label for="filt-sin-asig" class="text-sm cursor-pointer">Solo sin asignación al curso</label>
        </div>
      </div>

      <EstudyooLoadingBanner
        :visible="loading && !mapa"
        :message="modoCarga === 'todos' ? 'Trayendo todos los capítulos de la materia…' : 'Construyendo mapa filtrado…'"
      />

      <p v-if="!mapa && !loading" class="text-sm text-500 mb-3">
        Sin datos en pantalla. Elige materia y pulsa <strong>Traer todos</strong> o acota nivel/curso y pulsa <strong>Cargar mapa</strong>.
      </p>

      <template v-if="mapa">
        <div class="mb-3">
          <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-3">
            <div v-for="stat in statsCards" :key="stat.key" class="mapa-cap__stat p-3 border-round border-1 surface-border">
              <span class="text-2xl font-bold text-primary">{{ stat.value }}</span>
              <span class="block text-xs text-600">{{ stat.label }}</span>
            </div>
          </div>

          <p v-if="mapa.curso" class="text-sm text-600 mb-3">
            Curso: <strong>{{ labelCursoMapa(mapa.curso) }}</strong>
            <span v-if="mapa.curso.colegio_nombre"> · {{ mapa.curso.colegio_nombre }}</span>
          </p>
          <p v-else class="text-sm text-orange-700 mb-3">
            Sin curso seleccionado: ves actividades del catálogo, no asignaciones al alumno.
          </p>
        </div>

        <div v-if="!ejesFiltrados.length" class="p-4 border-round border-1 surface-border text-center text-600">
          <p class="m-0 mb-2">No hay capítulos que coincidan con los filtros.</p>
          <p v-if="mapa.resumen?.capitulos" class="text-sm text-500 m-0">
            El mapa tiene {{ mapa.resumen.capitulos }} capítulo(s) en total; prueba desactivar los filtros
            «solo sin actividades» / «solo sin asignación».
          </p>
          <p v-else class="text-sm text-500 m-0">
            Esta materia no tiene capítulos en Django. Verifica sync o elige otra asignatura del mismo colegio.
          </p>
        </div>

        <Accordion v-else :multiple="true" :active-index="activeEjes">
        <AccordionTab v-for="eje in ejesFiltrados" :key="eje.id">
          <template #header>
            <div class="flex flex-wrap align-items-center gap-2 w-full pr-2">
              <span class="font-semibold">{{ eje.nombre || eje.codigo }}</span>
              <TagPrime :value="`${eje.capitulos.length} cap.`" severity="info" class="text-xs" />
              <TagPrime
                v-if="conteoSinActEje(eje)"
                :value="`${conteoSinActEje(eje)} sin act.`"
                severity="warning"
                class="text-xs"
              />
            </div>
          </template>

          <div class="flex flex-column gap-2">
            <article
              v-for="cap in eje.capitulos"
              :key="cap.id"
              class="mapa-cap__chapter p-3 border-round border-1 surface-border"
              :class="{ 'mapa-cap__chapter--empty': cap.actividades.total === 0 }"
            >
              <div class="flex flex-wrap align-items-start justify-content-between gap-2">
                <div>
                  <h4 class="m-0 text-base font-semibold">{{ cap.nombre }}</h4>
                  <p class="text-xs text-500 m-0 mt-1">
                    <span v-if="cap.codigo">{{ cap.codigo }}</span>
                    <span v-if="cap.codigo && cap.nivel_simce"> · </span>
                    <span v-if="cap.nivel_simce">{{ labelNivel(cap.nivel_simce) }}</span>
                    <span v-if="cap.id_legacy"> · legacy #{{ cap.id_legacy }}</span>
                  </p>
                </div>
                <div class="flex flex-wrap gap-1">
                  <TagPrime
                    :value="`${cap.actividades.total} act.`"
                    :severity="cap.actividades.total ? 'success' : 'secondary'"
                  />
                  <TagPrime
                    v-if="cap.actividades.activas !== cap.actividades.total"
                    :value="`${cap.actividades.activas} activas`"
                    severity="info"
                  />
                  <TagPrime
                    v-if="mapa.curso && cap.asignaciones_curso"
                    :value="`${cap.asignaciones_curso.total} asig. curso`"
                    :severity="cap.asignaciones_curso.total ? 'help' : 'warning'"
                  />
                </div>
              </div>

              <div v-if="cap.actividades.por_tipo.length" class="mt-2">
                <p class="text-xs font-semibold text-600 mb-1 mt-0">Por tipo</p>
                <div class="flex flex-wrap gap-1">
                  <TagPrime
                    v-for="t in cap.actividades.por_tipo"
                    :key="t.codigo"
                    :value="`${t.nombre}: ${t.total}${t.activas !== t.total ? ` (${t.activas} activas)` : ''}`"
                    severity="secondary"
                    class="text-xs"
                  />
                </div>
              </div>
              <p v-else class="text-xs text-orange-600 mt-2 mb-0">Sin actividades en este capítulo.</p>

              <div v-if="mapa.curso && cap.actividades.total && !cap.asignaciones_curso?.total" class="mt-2">
                <p class="text-xs text-orange-700 m-0">
                  Hay actividades pero ninguna asignada a este curso.
                  <ButtonPrime
                    label="Ir a asignar"
                    icon="pi pi-share-alt"
                    class="p-button-link p-0 text-xs ml-1"
                    @click="irAsignaciones"
                  />
                </p>
              </div>
            </article>
          </div>
        </AccordionTab>
        </Accordion>
      </template>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import ButtonPrime from 'primevue/button';
import Dropdown from 'primevue/dropdown';
import Checkbox from 'primevue/checkbox';
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import TagPrime from 'primevue/tag';
import DjangoAuthGate from '@/components/estudyoo/DjangoAuthGate.vue';
import EstudyooLoadingBanner from '@/components/estudyoo/EstudyooLoadingBanner.vue';
import { ActividadesApi } from '@/services/EstudyooAdminApi';
import { isDjangoAdminAuthenticated, handleDjangoApiError } from '@/services/djangoAdminAuth';
import { compararCursos, labelCurso, labelNivel, NIVEL_ORDEN } from '@/utils/nivelesEscolares';

const router = useRouter();
const toast = useToast();
const djangoOk = ref(isDjangoAdminAuthenticated());

const colegios = ref([]);
const materias = ref([]);
const cursos = ref([]);
const mapa = ref(null);

const selColegioId = ref(null);
const selMateria = ref(null);
const selNivel = ref(null);
const selCurso = ref(null);

const loading = ref(false);
const loadingColegios = ref(false);
const loadingCatalogos = ref(false);
const catalogListo = ref(false);
const modoCarga = ref('');
const soloSinActividades = ref(false);
const soloSinAsignacion = ref(false);

const materiasFiltradas = computed(() => {
  if (!selColegioId.value) return materias.value;
  return materias.value.filter(
    (m) => !m.colegio_id || String(m.colegio_id) === String(selColegioId.value),
  );
});

const colegioOpts = computed(() =>
  colegios.value.map((c) => ({
    id: c.id,
    label: c.label || c.nombre || `Colegio ${String(c.id).slice(0, 8)}`,
  })),
);

const cursosFiltrados = computed(() => {
  if (!selColegioId.value) return cursos.value;
  return cursos.value.filter((c) => String(c.colegio_id || c.colegio) === String(selColegioId.value));
});

const nivelOpts = computed(() => {
  const disponibles = new Set(cursosFiltrados.value.map((c) => c.nivel_simce).filter(Boolean));
  return NIVEL_ORDEN.filter((n) => disponibles.has(n)).map((value) => ({
    value,
    label: labelNivel(value),
  }));
});

const cursoOpts = computed(() => {
  const base = [...cursosFiltrados.value];
  const filtered = selNivel.value
    ? base.filter((c) => c.nivel_simce === selNivel.value)
    : base;
  return filtered.sort(compararCursos).map((c) => ({ ...c, label: labelCurso(c) }));
});

const filtrosActivos = computed(() => !!mapa.value);

const statsCards = computed(() => {
  const r = mapa.value?.resumen;
  if (!r) return [];
  const cards = [
    { key: 'ejes', label: 'Ejes / unidades', value: r.ejes },
    { key: 'capitulos', label: 'Capítulos', value: r.capitulos },
    { key: 'con_act', label: 'Con actividades', value: r.capitulos_con_actividades },
    { key: 'sin_act', label: 'Sin actividades', value: r.capitulos_sin_actividades },
    { key: 'act', label: 'Actividades', value: r.actividades_total },
  ];
  if (mapa.value?.curso) {
    cards.push({ key: 'asig', label: 'Asignadas al curso', value: r.asignaciones_curso });
  }
  return cards;
});

function filtrarCapitulo(cap) {
  if (soloSinActividades.value && cap.actividades.total > 0) return false;
  if (soloSinAsignacion.value && mapa.value?.curso) {
    const n = cap.asignaciones_curso?.total || 0;
    if (n > 0) return false;
  }
  return true;
}

const ejesFiltrados = computed(() => {
  if (!mapa.value?.ejes) return [];
  return mapa.value.ejes
    .map((eje) => ({
      ...eje,
      capitulos: (eje.capitulos || []).filter(filtrarCapitulo),
    }))
    .filter((e) => e.capitulos.length > 0);
});

const activeEjes = computed(() => ejesFiltrados.value.map((_, i) => i));

function conteoSinActEje(eje) {
  return (eje.capitulos || []).filter((c) => !c.actividades?.total).length;
}

function labelCursoMapa(c) {
  return labelCurso({
    nivel_simce: c.nivel_simce,
    letra: c.letra,
    nombre_interno: c.nombre_interno,
  });
}

function onDjangoAuth(ok) {
  djangoOk.value = !!ok;
  if (ok) cargarColegios();
}

async function cargarCatalogosSiFalta() {
  if (catalogListo.value || loadingCatalogos.value) return;
  await cargarCatalogos();
}

function onDjangoUnauthorized() {
  djangoOk.value = false;
  mapa.value = null;
}

function catchApi(e, ctx) {
  if (handleDjangoApiError(e, { toast, onUnauthorized: onDjangoUnauthorized })) return;
  toast.add({
    severity: 'error',
    summary: ctx || 'Error',
    detail: e.response?.data?.detail || e.message || 'Error desconocido',
    life: 6000,
  });
}

async function cargarColegios() {
  loadingColegios.value = true;
  try {
    const data = await ActividadesApi.listColegios({ page_size: 500 });
    const lista = Array.isArray(data) ? data : data.results || [];
    colegios.value = lista
      .filter((c) => c && c.id)
      .map((c) => ({
        id: c.id,
        nombre: c.nombre || c.Nombre,
        label: c.nombre || c.Nombre || `Colegio ${String(c.id).slice(0, 8)}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'es'));
  } catch (e) {
    colegios.value = [];
    catchApi(e, 'Colegios');
  } finally {
    loadingColegios.value = false;
  }
}

async function cargarCatalogos() {
  loadingCatalogos.value = true;
  try {
    const [m, c] = await Promise.all([
      ActividadesApi.listMaterias(
        selColegioId.value ? { colegio_id: selColegioId.value } : {},
      ),
      ActividadesApi.listCursos(selColegioId.value ? { colegio_id: selColegioId.value } : {}),
    ]);
    materias.value = m;
    cursos.value = c;
    catalogListo.value = true;
    if (selMateria.value && !materiasFiltradas.value.some((x) => x.id === selMateria.value.id)) {
      selMateria.value = null;
      selNivel.value = null;
      selCurso.value = null;
    }
  } catch (e) {
    catchApi(e, 'Catálogo');
  } finally {
    loadingCatalogos.value = false;
  }
}

function onColegioChange() {
  selMateria.value = null;
  selNivel.value = null;
  selCurso.value = null;
  mapa.value = null;
  catalogListo.value = false;
  materias.value = [];
  cursos.value = [];
}

function onMateriaChange() {
  selNivel.value = null;
  selCurso.value = null;
  mapa.value = null;
}

function onNivelChange() {
  selCurso.value = null;
  mapa.value = null;
}

function limpiar() {
  selColegioId.value = null;
  selMateria.value = null;
  selNivel.value = null;
  selCurso.value = null;
  mapa.value = null;
  modoCarga.value = '';
  soloSinActividades.value = false;
  soloSinAsignacion.value = false;
  catalogListo.value = false;
  materias.value = [];
  cursos.value = [];
}

async function traerTodos() {
  await fetchMapa({ todos: true });
}

async function cargarMapa() {
  await fetchMapa({ todos: false });
}

async function fetchMapa({ todos }) {
  if (!selMateria.value?.id) {
    toast.add({ severity: 'warn', summary: 'Filtros', detail: 'Elige una materia.', life: 4000 });
    return;
  }
  if (!todos && !selNivel.value) {
    toast.add({
      severity: 'warn',
      summary: 'Filtros',
      detail: 'Elige un nivel o usa «Traer todos» para ver toda la materia.',
      life: 5000,
    });
    return;
  }

  await cargarCatalogosSiFalta();

  loading.value = true;
  modoCarga.value = todos ? 'todos' : 'filtrado';
  mapa.value = null;
  try {
    const params = { materia_id: selMateria.value.id };
    if (!todos && selNivel.value) params.nivel_simce = selNivel.value;
    if (selCurso.value?.id) params.curso_id = selCurso.value.id;
    const data = await ActividadesApi.getMapaCapitulos(params);
    if (!data || typeof data !== 'object' || !Array.isArray(data.ejes)) {
      throw new Error('Respuesta inválida del mapa de capítulos.');
    }
    mapa.value = data;
  } catch (e) {
    catchApi(e, 'Mapa capítulos');
  } finally {
    loading.value = false;
    modoCarga.value = '';
  }
}

function irAsignaciones() {
  router.push({ name: 'estudyoo-asignaciones' });
}

onMounted(() => {
  if (djangoOk.value) cargarColegios();
});
</script>

<style scoped>
.mapa-cap {
  max-width: 1100px;
}

.mapa-cap__stat {
  background: var(--surface-50, #f8fafc);
}

.mapa-cap__chapter {
  background: #fff;
}

.mapa-cap__chapter--empty {
  border-color: #fed7aa !important;
  background: #fffbeb;
}
</style>
